import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

import logo from '/Logo_dashboard.webp';
import EnquiryModal from './pages/EnquiryModal';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [lastOpenedFaq, setLastOpenedFaq] = useState(0);
  const [showModal, setShowModal] = useState(false); // <--- ADDED STATE

  // email submit section


  // FAQ section animation states
  const [isFaqImageAnimated, setIsFaqImageAnimated] = useState(false);
  const [isFaqContentAnimated, setIsFaqContentAnimated] = useState(false);
  const [activeFaq, setActiveFaq] = useState(0); // First FAQ open by default
  const videoRef = useRef(null);

  // FAQ section refs
  const faqImageRef = useRef(null);
  const faqContentRef = useRef(null);

  // FAQ data array (kept 5 items; only text updated to Policy Saath)
  const faqData = [
    {
      question: "How to Register ?",
      answer: "Visit the PolicySaath portal, choose your role (Agent/Customer), enter your details, accept the terms, and submit to log in."
    },
    {
      question: "Select Your Insurance Category ?",
      answer: "Use filters or menus to view policies by category (health, life, motor, business, etc.). PolicySaath keeps all categories organized for fast lookup."
    },
    {
      question: "How to import Policies with Excel file?",
      answer: "Go to ‘Import Policy’, download the sample, fill your records (Policy No, Insured, Premium, Dates), upload the file, review and confirm."
    },
    {
      question: "How to add policy with manually form ?",
      answer: "Open ‘Add New Policy’, fill required details, link to an existing customer/company (or create new), then save. It appears instantly in lists and dashboard."
    },
    {
      question: "How to add Family Member?",
      answer: "From the ‘Family’ tab, create/link members under one Family ID. Assign policies per member and track combined renewals together."
    }
  ];

  // FAQ images array (kept same length & filenames you already use)
  const faqImages = [
    "/FAQ/Login.webp",
    "/FAQ/Choose_company.webp",
    "/FAQ/import-policy.webp",
    "/FAQ/manually_form.webp",
    "/FAQ/Add-family.webp"
  ];

  // FAQ click handler
  // const handleFaqClick = (index) => {
  //   setActiveFaq(activeFaq === index ? -1 : index); // Toggle or close if same FAQ clicked
  // };
  const handleFaqClick = (index) => {
    if (activeFaq === index) {
      // closing the same accordion – keep lastOpenedFaq same
      setActiveFaq(-1);
    } else {
      // opening a new accordion – update both activeFaq and lastOpenedFaq
      setActiveFaq(index);
      setLastOpenedFaq(index);
    }
  };
  const currentFaqImageIndex = activeFaq !== -1 ? activeFaq : lastOpenedFaq;

  // Add this useEffect for FAQ section animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;

          // Animate FAQ image column
          if (target === faqImageRef.current && !isFaqImageAnimated) {
            setTimeout(() => {
              setIsFaqImageAnimated(true);
            }, 100);
          }

          // Animate FAQ content column
          if (target === faqContentRef.current && !isFaqContentAnimated) {
            setTimeout(() => {
              setIsFaqContentAnimated(true);
            }, 200);
          }

          observer.unobserve(target);
        }
      });
    }, observerOptions);

    if (faqImageRef.current) {
      observer.observe(faqImageRef.current);
    }
    if (faqContentRef.current) {
      observer.observe(faqContentRef.current);
    }

    return () => {
      if (faqImageRef.current) {
        observer.unobserve(faqImageRef.current);
      }
      if (faqContentRef.current) {
        observer.unobserve(faqContentRef.current);
      }
    };
  }, [isFaqImageAnimated, isFaqContentAnimated]);

  // Animation states for services section
  const [isHeaderAnimated, setIsHeaderAnimated] = useState(false);
  const [animatedCards, setAnimatedCards] = useState([false, false, false, false]);

  // Animation states for Who We Are section
  const [isImageAnimated, setIsImageAnimated] = useState(false);
  const [isContentAnimated, setIsContentAnimated] = useState(false);

  // Statistics section animation states
  const [isStatsTextAnimated, setIsStatsTextAnimated] = useState(false);
  const [isStatsDescAnimated, setIsStatsDescAnimated] = useState(false);
  const [areStatsAnimated, setAreStatsAnimated] = useState(false);

  // Counter animation states (unchanged logic/targets can be tuned)
  const [experienceCount, setExperienceCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);
  const [specialistsCount, setSpecialistsCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);

  // Refs for scroll animation targets
  const servicesHeaderRef = useRef(null);
  const servicesGridRef = useRef(null);
  const imageColumnRef = useRef(null);
  const contentColumnRef = useRef(null);
  const statsTextRef = useRef(null);
  const statsDescRef = useRef(null);
  const statsGridRef = useRef(null);

  // Typing texts (kept behavior; updated slogans only)
  const typingTexts = [
    "Simplify Policy Management Digitally",
    "Smart Platform for Agents and Customers",
    "Track, Renew, and Manage All in One Place"
  ];

  // Counter animation functions
  const startCounterAnimations = () => {
    const counters = [
      { setter: setExperienceCount, target: 5, duration: 2000 },
      { setter: setProjectsCount, target: 95, duration: 2500 },
      { setter: setSpecialistsCount, target: 20, duration: 2200 },
      { setter: setClientsCount, target: 1000, duration: 3000 }
    ];

    counters.forEach(({ setter, target, duration }, index) => {
      setTimeout(() => {
        animateCounter(setter, target, duration);
      }, index * 200);
    });
  };

  const animateCounter = (setter, target, duration) => {
    const startTime = Date.now();
    const startValue = 0;

    const updateCounter = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (target - startValue) * easeOut);

      setter(currentValue);

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        setter(target);
      }
    };

    requestAnimationFrame(updateCounter);
  };

  // Parallax Scroll (only for background)
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typing Effect
  useEffect(() => {
    const currentText = typingTexts[textIndex];
    if (charIndex < currentText.length) {
      const timeout = setTimeout(() => {
        setDisplayText((prev) => prev + currentText[charIndex]);
        setCharIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setCharIndex(0);
        setDisplayText("");
        setTextIndex((prev) => (prev + 1) % typingTexts.length);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, textIndex]);

  // Scroll-triggered animations for services section
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;

          // Animate services header
          if (target === servicesHeaderRef.current && !isHeaderAnimated) {
            setTimeout(() => {
              setIsHeaderAnimated(true);
            }, 100);
          }

          // Animate services cards
          if (target === servicesGridRef.current && !animatedCards.some(card => card)) {
            // Animate cards with staggered delays
            setTimeout(() => {
              setAnimatedCards(prev => {
                const newState = [...prev];
                newState[0] = true;
                return newState;
              });
            }, 200);

            setTimeout(() => {
              setAnimatedCards(prev => {
                const newState = [...prev];
                newState[1] = true;
                return newState;
              });
            }, 350);

            setTimeout(() => {
              setAnimatedCards(prev => {
                const newState = [...prev];
                newState[2] = true;
                return newState;
              });
            }, 500);

            setTimeout(() => {
              setAnimatedCards(prev => {
                const newState = [...prev];
                newState[3] = true;
                return newState;
              });
            }, 650);
          }

          // Stop observing once animated
          observer.unobserve(target);
        }
      });
    }, observerOptions);

    // Start observing
    if (servicesHeaderRef.current) {
      observer.observe(servicesHeaderRef.current);
    }
    if (servicesGridRef.current) {
      observer.observe(servicesGridRef.current);
    }

    // Cleanup
    return () => {
      if (servicesHeaderRef.current) {
        observer.unobserve(servicesHeaderRef.current);
      }
      if (servicesGridRef.current) {
        observer.unobserve(servicesGridRef.current);
      }
    };
  }, [isHeaderAnimated, animatedCards]);

  // Scroll-triggered animations for Who We Are section
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;

          // Animate image column
          if (target === imageColumnRef.current && !isImageAnimated) {
            setTimeout(() => {
              setIsImageAnimated(true);
            }, 100);
          }

          // Animate content column
          if (target === contentColumnRef.current && !isContentAnimated) {
            setTimeout(() => {
              setIsContentAnimated(true);
            }, 200);
          }

          // Stop observing once animated
          observer.unobserve(target);
        }
      });
    }, observerOptions);

    // Start observing
    if (imageColumnRef.current) {
      observer.observe(imageColumnRef.current);
    }
    if (contentColumnRef.current) {
      observer.observe(contentColumnRef.current);
    }

    // Cleanup
    return () => {
      if (imageColumnRef.current) {
        observer.unobserve(imageColumnRef.current);
      }
      if (contentColumnRef.current) {
        observer.unobserve(contentColumnRef.current);
      }
    };
  }, [isImageAnimated, isContentAnimated]);

  // Scroll-triggered animations for Statistics section
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;

          // Animate stats text
          if (target === statsTextRef.current && !isStatsTextAnimated) {
            setTimeout(() => {
              setIsStatsTextAnimated(true);
            }, 100);
          }

          // Animate stats description
          if (target === statsDescRef.current && !isStatsDescAnimated) {
            setTimeout(() => {
              setIsStatsDescAnimated(true);
            }, 200);
          }

          // Animate stats grid and start counters
          if (target === statsGridRef.current && !areStatsAnimated) {
            setTimeout(() => {
              setAreStatsAnimated(true);
              startCounterAnimations();
            }, 400);
          }

          observer.unobserve(target);
        }
      });
    }, observerOptions);

    if (statsTextRef.current) {
      observer.observe(statsTextRef.current);
    }
    if (statsDescRef.current) {
      observer.observe(statsDescRef.current);
    }
    if (statsGridRef.current) {
      observer.observe(statsGridRef.current);
    }

    return () => {
      if (statsTextRef.current) {
        observer.unobserve(statsTextRef.current);
      }
      if (statsDescRef.current) {
        observer.unobserve(statsDescRef.current);
      }
      if (statsGridRef.current) {
        observer.unobserve(statsGridRef.current);
      }
    };
  }, [isStatsTextAnimated, isStatsDescAnimated, areStatsAnimated]);

  const sectionRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsImageAnimated(true);
          setIsContentAnimated(true);

          // Play video when in view
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.log('Video autoplay failed:', err);
            });
            setIsPlaying(true);
          }
        } else {
          // Pause when out of view
          if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Toggle mute/unmute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Toggle play/pause manually
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <>
      {/* Header Section (unchanged structure) */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <Link to="/">
                <div className="logo-icon">
                  <img src={logo} alt="PolicySaath Logo" className='logo-icon'></img>
                </div>
                {/* <span className="logo-text">POLICYSAATH</span> */}
              </Link>
            </div>

            {/* Navigation */}
            <nav className="navigation">
              <ul className="nav-links">
                <li><Link to="/" className="nav-link active">Home</Link></li>
                <li>
                  <Link to="/about-us" className="nav-link">About Us</Link>
                </li>
                <li>
                  <Link to="/contact-us" className="nav-link">Contact Us</Link>
                </li>
                <li>
                  <Link to="/subscription" className="nav-link">Subscription</Link>
                </li>
                </ul>
            </nav>

            {/* Buttons Section */}
            <div className="header-buttons">
              <Link to="/login" className="btn-login">
                Login
              </Link>
              <Link to="/register" className="btn-register">
                Register
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Section (structure unchanged, text updated) */}
      <section className="banner">
        {/* Background with parallax */}
        <div
          className="banner-bg"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>

        <div className="container">
          <div className="banner-content">
            <div className="banner-center">
              <h1 className="banner-title">
                {displayText}
                <span className="cursor">|</span>
              </h1>
              <p className="banner-subtitle">
                PolicySaath is an intelligent, web-based insurance management and client data platform
                for agents, agencies, and companies — centralizing policy records, premium tracking,
                renewals, and notifications in one secure place.
              </p>
              <div className="banner-center">
                <div className="header-buttons">
                  <Link to="/" className="btn-login"
                  onClick={() => setShowModal(true)}
                  >
                    Book a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Curved Bottom */}
        <div className="banner-curve">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Services Section with Scroll Animations (kept 4 cards & icons; copy updated only) */}

      <section className="insurance-services-section">
        <div className="container">
          <div ref={servicesHeaderRef} className={`services-header ${isHeaderAnimated ? "animate-in" : ""}`}>
            <span className="sub-title">FEATURES</span>
            <h2>Core <span>PolicySaath</span> Highlights</h2>
            <p>Explore key features that help agents and customers manage insurance effortlessly.</p>
          </div>
          <div ref={servicesGridRef} className="services-grid">
            <div className={`service-card ${animatedCards[0] ? "animate-in" : ""}`}>
              <div className="service-icon"><i className="bi bi-cloud-upload"></i></div>
              <h3>Import Policies</h3>
              <p>Import multiple policies through Excel in seconds — fast, accurate, and secure.</p>
            </div>
            <div className={`service-card featured-card ${animatedCards[1] ? "animate-in" : ""}`}>
              <div className="service-icon"><i className="bi bi-speedometer"></i></div>
              <h3>Dashboard Overview</h3>
              <p>Track total companies, customers, and premiums with smart visual analytics.</p>
            </div>
            <div className={`service-card ${animatedCards[2] ? "animate-in" : ""}`}>
              <div className="service-icon"><i className="bi bi-people"></i></div>
              <h3>Family Management</h3>
              <p>Link and manage multiple family members under one group for better tracking.</p>
            </div>
            <div className={`service-card ${animatedCards[3] ? "animate-in" : ""}`}>
              <div className="service-icon"><i className="bi bi-bell"></i></div>
              <h3>Smart Notifications</h3>
              <p>Never miss a renewal — automatic reminders for all due and expired policies.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section (layout identical; content updated) */}
      <section id="who-we-are" className="who-we-are-section" ref={sectionRef}>
        <h2 className='heading'>Who <span className='spanwe'>WE</span> are?</h2>
        <div className="container">
          <div className="who-we-are-content">

            {/* Left Column - Video */}
            <div className={`video-column ${isImageAnimated ? 'animate-in' : ''}`}>
              <div className="video-container">
                <div className="video-wrapper">
                  <video
                    ref={videoRef}
                    className="main-video"
                    loop
                    muted
                    playsInline
                    preload="auto"
                    poster="/path-to-a-static-placeholder-image.jpg" // Shows an image if video fails
                    onError={(e) => console.error("Video failed to load:", e)}
                  >
                    <source src="https://excelupload-bucket.s3.ap-south-1.amazonaws.com/home_video.mp4" type="video/mp4" />

                    {/* Add this track element to fix the accessibility bug */}
                    <track
                      kind="captions"
                      src="/path/to/your/captions.vtt"
                      srcLang="en"
                      label="English"
                      default
                    />

                    Your browser does not support the video tag.
                  </video>

                  {/* Video Controls Overlay */}
                  <div className="video-controls">
                    <button
                      className="control-btn play-pause-btn"
                      onClick={togglePlayPause}
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="currentColor" />
                        </svg>
                      )}
                    </button>

                    <button
                      className="control-btn volume-btn"
                      onClick={toggleMute}
                      aria-label={isMuted ? 'Unmute' : 'Mute'}
                    >
                      {isMuted ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Decorative elements */}
                  <div className="video-glow"></div>
                  <div className="video-border"></div>
                </div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div
              ref={contentColumnRef}
              className={`content-column ${isContentAnimated ? 'animate-in' : ''}`}
            >
              {/* <span className="section-subtitle">WHO WE ARE</span> */}

              <h2 className="section-title">
                Stop tracking policies manually. Start managing them intelligently</h2>


              <blockquote className="highlight-quote">
                PolicySaath centralizes data, automates reminders, and gives agents full control — without spreadsheets or paperwork.
              </blockquote>

              <p className="section-description">
                Built for insurance agents and organizations, PolicySaath lets you import policies in bulk,
                add new records quickly, group families under one ID, track premiums due/collected, and stay on top of
                renewals with notifications. Access everything from a clean dashboard, filter by company or category,
                and export reports anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section (kept counters & contact sub-section) */}
      <section className="statistics-section">
        <div className="container">
          <div className="statistics-content">
            <div
              ref={statsTextRef}
              className={`stats-text ${isStatsTextAnimated ? 'animate-in' : ''}`}
            >
              <h2 className="stats-title">
                The Right Platform to Manage Your Insurance Workflows
              </h2>
            </div>

            <div
              ref={statsDescRef}
              className={`stats-description ${isStatsDescAnimated ? 'animate-in' : ''}`}
            >
              <p>
                PolicySaath brings together policy storage, premium tracking, renewal reminders, and customer communication
                so you can work faster and with complete accuracy.
              </p>
            </div>
          </div>

          <div
            ref={statsGridRef}
            className={`stats-grid ${areStatsAnimated ? 'animate-in' : ''}`}
          >
            <div className="stat-item">
              <div className="stat-card">
                <div className="stat-number">
                  <span className="counter">{experienceCount}</span>
                  <span className="stat-symbol">+</span>
                </div>
                <p className="stat-label">Years in Service</p>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-card">
                <div className="stat-number">
                  <span className="counter">{projectsCount}</span>
                  <span className="stat-symbol">%</span>
                </div>
                <p className="stat-label">Data Accuracy</p>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-card">
                <div className="stat-number">
                  <span className="counter">{specialistsCount}</span>
                  <span className="stat-symbol">+</span>
                </div>
                <p className="stat-label">Key Features</p>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-card">
                <div className="stat-number">
                  <span className="counter">{clientsCount}</span>
                  <span className="stat-symbol">+</span>
                </div>
                <p className="stat-label">Policies Managed</p>
              </div>
            </div>
          </div>

          {/* Contact Section (kept exactly in place) */}
          <div className="contact-section">
            <div className="contact-item">
              <span className="contact-label">Call Us Now:</span>
              <div className="contact-info">
                <a href="tel:+91 9136356555" className="contact-link">
                  +91 9136356555
                </a>
                <div className="contact-icon">
                  <i className="bi bi-telephone"></i>
                </div>
              </div>
            </div>

            <div className="contact-item">
              <span className="contact-label">Email Us:</span>
              <div className="contact-info">
                <a href="policysaath@gmail.com" className="contact-link">
                  policysaath@gmail.com
                </a>
                <div className="contact-icon">
                  <i className="bi bi-envelope"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (unchanged structure; copy aligned to doc) */}
      <section id="faqs" className="faq-section">
        <div className="container">
          {/* FAQ Section Header */}
          <div className="faq-section-header">
            <span className="faq-section-subtitle">FAQS</span>
            <h2 className="faq-section-title">
              Frequently <span>asked questions</span>
            </h2>
            <p className="faq-section-description">
              Quick answers about registration, importing data, adding policies, managing families, and using the dashboard.
            </p>
          </div>

          <div className="faq-content">
            {/* Left Column - Image and Features */}
            <div
              ref={faqImageRef}
              className={`faq-left ${isFaqImageAnimated ? 'animate-in' : ''}`}
            >
              {/* Dynamic FAQ Image */}
              <div className="faq-image-container">
                <img
                  src={faqImages[currentFaqImageIndex]}
                  alt="FAQ Illustration"
                  className="faq-image"
                  width="536" 
                  height="536"
                  loading="lazy"
                />

              </div>

              {/* Features List (kept structure; tightened copy) */}
              <div className="faq-features">
                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bi bi-check-circle"></i>
                  </div>
                  <div className="feature-content">
                    <div className='feature-heading'>Centralized Records</div>
                    <p>All client, company, and policy data in one secure dashboard.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bi bi-headset"></i>
                  </div>
                  <div className="feature-content">
                    <div className='feature-heading'>Smart Reminders</div>
                    <p>Automated renewal notifications so you never miss a due date.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - FAQ Content */}
            <div
              ref={faqContentRef}
              className={`faq-right ${isFaqContentAnimated ? 'animate-in' : ''}`}
            >
              {/* FAQ Accordion */}
              <div className="faq-accordion">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className={`faq-item ${activeFaq === index ? 'active' : ''}`}

                  >
                    <div
                      className="faq-question"
                      onClick={() => handleFaqClick(index)}
                    >
                      <span className="faq-number">{index + 1}.</span>
                      <h3>{faq.question}</h3>
                      <div className="faq-toggle">
                        <i className={`bi ${activeFaq === index ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                      </div>
                    </div>

                    <div className={`faq-answer ${activeFaq === index ? 'expanded' : ''}`}>
                      <div className="faq-answer-content">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Footer (kept full structure incl. newsletter & 3 columns) */}
      <footer className="footer">
        <div className="footer-top" style={{ paddingTop: "40px" }}>
          {/* Left - Logo & Description */}
          <div className="footer-brand">
            <h2 className="footer-logo"><div className="logo-icon text-cente">
              <img src={logo} alt="PolicySaath Logo" style={{ height: "70px" }}></img>
            </div></h2>
            <p>
              PolicySaath helps agents, agencies, and companies centralize policy records,
              track premiums, and manage renewals with accuracy and ease.
            </p>
            
          <div className="social-icons">
            <a href="https://www.facebook.com/policysaath" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
            <a href="https://www.instagram.com/policysaath/" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
            <a href="https://www.youtube.com/@PolicySaath" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
            <a href="#" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
          </div>
          </div>

          {/* Middle Columns */}
          <div className="footer-links">
            <div className="footer-col">
              <h4>Services</h4>
              <ul>
                <li><a href="#">Dashboard</a></li>
                <li><a href="#">Import Policy</a></li>
                <li><a href="#">Add New Policy</a></li>
                <li><a href="#">Family Management</a></li>
                <li><a href="#">Notifications</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Support</h4>
              <ul>
                <li><a href="#">Help Center</a></li>
                <li><a href="#">Ticket Support</a></li>
                <li><a href="#">FAQ</a></li>
                <li><a href="#">Contact us</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><a href="#">About us</a></li>
                <li><a href="#">Leadership</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Article & News</a></li>
                <li><a href="#">Legal Notices</a></li>
              </ul>
            </div>
          </div>

          <div id="contact-us" className="footer-newsletter">
            <h1>Contact Us</h1>
            <p>
              Our Team will reach out to you as soon as possible on your mail
            </p>
            <div className="newsletter-form">
              {/* Email input is removed */}
              <button
                type="button"
                onClick={() => setShowModal(true)} // <-- Function to open modal
              >
                <i className="fas fa-paper-plane"></i> Enquiry Now
              </button>
            </div>
          </div>

          {/* // ADDED: RENDER THE NEW MODAL COMPONENT just before closing  */}
          <EnquiryModal
            show={showModal}
            onClose={() => setShowModal(false)}
          />
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p>Copyright © 2025 PolicySaath, All rights reserved.</p>
          <div className="footer-bottom-links">
            <a href="/terms-conditions">Terms & Conditions</a>
            <a href="/privacy-policy">Privacy policy</a>
            <a href="disclaimer">Disclaimer</a>
          </div>
        </div>
      </footer>

    </>
  );
};

export default Home;
